import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useGovernance } from './GovernanceProvider'
import { moduleActions, UNIVERSAL_ACTIONS, type GovModuleAction } from './governanceActions'
import {
  StatusPill, SavedStatusBadge, SeverityDot, Panel, formatWhen, STATUS_KIND_VAR,
} from './governanceUi'
import { GOV_MODULE_DEFS, govRecordPath, type GovModule } from './governanceCards'
import {
  evaluatePermission, effectiveRole, type GovRecord, type GovAction, type PermissionDecision,
} from './governanceModel'

const TABS = ['Overview', 'Activity', 'Analysis', 'Related', 'Evidence', 'Permissions', 'History'] as const
type Tab = (typeof TABS)[number]

export interface UniversalRecordWorkspaceProps {
  module: GovModule
  recordId: string
  onBack: () => void
  onOpenRecord: (module: GovModule, recordId: string) => void
  /** notify the shell/host that a link was copied (SSR-safe host handles clipboard) */
  onCopyLink?: (path: string) => void
}

/** A button that is either performable or DISABLED WITH A VISIBLE REASON. */
function ActionButton({
  label, decision, onClick, variant, gate,
}: { label: string; decision?: PermissionDecision; onClick: () => void; variant?: 'primary' | 'danger'; gate?: string }) {
  const blockedReason = decision && !decision.allowed ? decision.reason : gate
  const disabled = Boolean(blockedReason)
  return (
    <button
      type="button"
      className="olv-gov-btn"
      data-variant={variant}
      onClick={onClick}
      disabled={disabled}
      title={disabled ? `Unavailable — ${blockedReason}` : label}
      aria-disabled={disabled}
    >
      {label}{disabled ? ' (locked)' : ''}
    </button>
  )
}

const railRowStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, paddingBottom: 10, borderBottom: '1px solid var(--govv-line)', marginBottom: 10 }
const railLabelStyle: CSSProperties = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--govv-ink-3)', fontFamily: 'var(--sc-font-mono)' }

/**
 * UniversalRecordWorkspace — the consistent full working surface opened from any
 * governance record. Overview / Activity / Analysis / Related / Evidence /
 * Permissions / History tabs, a collaboration right rail, and a composer. Every
 * action runs through the governance provider; disabled actions explain the
 * missing permission. Analysis is DETERMINISTIC (computed from state) — never
 * fabricated AI.
 */
export default function UniversalRecordWorkspace({
  module, recordId, onBack, onOpenRecord, onCopyLink,
}: UniversalRecordWorkspaceProps) {
  const gov = useGovernance()
  const [tab, setTab] = useState<Tab>('Overview')
  const [composer, setComposer] = useState('')
  const [assignInput, setAssignInput] = useState('')
  const [reviewerInput, setReviewerInput] = useState('')
  const [labelInput, setLabelInput] = useState('')
  const [dueInput, setDueInput] = useState('')
  const [shareRecipient, setShareRecipient] = useState('')
  const [shareConstraint, setShareConstraint] = useState('view_only_7d')

  const rec = gov.record(recordId)
  if (!rec) {
    return (
      <Panel>
        <p style={{ color: 'var(--govv-ink-2)', margin: 0 }}>
          No record <code>{recordId}</code> in {GOV_MODULE_DEFS[module].title}. It may have been reset this session.
        </p>
        <button type="button" className="olv-gov-btn" onClick={onBack} style={{ marginTop: 12 }}>← Back to {GOV_MODULE_DEFS[module].title}</button>
      </Panel>
    )
  }

  const role = effectiveRole(gov.state)
  const actions = moduleActions(module)
  const primary = actions.find((a) => a.primary)
  const overflow = actions.filter((a) => !a.primary)
  const watching = rec.watchers.includes(gov.state.actor.name)
  const related = gov.correlated(rec.correlationId).filter((r) => r.id !== rec.id)
  const evidence = related.filter((r) => r.module === 'receipts' || r.module === 'integrity')

  const runGoverned = (a: GovModuleAction) => {
    gov.run({ type: 'governed', recordId: rec.id, action: a.action, label: a.label, patch: a.patch, severity: a.severity, denied: a.denied })
  }
  const decisionFor = (action: GovAction) => evaluatePermission(role, action)

  const copyLink = () => {
    const path = govRecordPath(module, rec.id)
    if (onCopyLink) { onCopyLink(path); return }
    if (typeof navigator !== 'undefined' && navigator.clipboard) navigator.clipboard.writeText(path).catch(() => undefined)
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="olv-gov-btn" onClick={onBack} style={{ padding: '4px 8px' }}>←</button>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: STATUS_KIND_VAR[rec.statusKind] }}>{rec.title}</h2>
            <StatusPill kind={rec.statusKind} label={rec.status} />
            <SavedStatusBadge status={rec.savedStatus} />
            <SeverityDot severity={rec.severity} />
          </div>
          <p style={{ margin: '6px 0 0', color: 'var(--govv-ink-2)', fontSize: 13 }}>{rec.summary}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {primary && (
            <ActionButton label={primary.label} decision={decisionFor(primary.action)} variant="primary" onClick={() => runGoverned(primary)} />
          )}
          <ActionButton label={watching ? 'Watching' : 'Watch'} onClick={() => gov.run({ type: watching ? 'unwatch' : 'watch', recordId: rec.id })} />
          <ActionButton label="Copy link" onClick={copyLink} />
        </div>
      </div>

      {/* Overflow governed actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {overflow.map((a) => (
          <ActionButton key={a.key} label={a.label} decision={decisionFor(a.action)} variant={a.denied ? 'danger' : undefined} onClick={() => runGoverned(a)} />
        ))}
        <ActionButton label="Escalate to risk" decision={decisionFor('escalate')} onClick={() => gov.run({ type: 'create_related', fromId: rec.id, input: { module: 'risks', title: `Follow-up · ${rec.title}`, summary: `Raised from ${GOV_MODULE_DEFS[module].title}`, statusKind: 'attention', severity: 'medium' } })} />
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label="Record sections" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--govv-line)', marginBottom: 16, overflowX: 'auto' }}>
        {TABS.map((t) => (
          <button
            key={t} role="tab" aria-selected={tab === t} type="button"
            onClick={() => setTab(t)}
            style={{
              padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer',
              color: tab === t ? 'var(--govv-identity)' : 'var(--govv-ink-3)', fontSize: 13, fontWeight: 600,
              borderBottom: tab === t ? '2px solid var(--govv-identity)' : '2px solid transparent', whiteSpace: 'nowrap',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="olv-gov-record-cols" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 16, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
          {tab === 'Overview' && <OverviewTab rec={rec} />}
          {tab === 'Activity' && (
            <ActivityTab
              rec={rec}
              composer={composer} setComposer={setComposer}
              canComment={decisionFor('comment')} canUpdate={decisionFor('add_update')}
              onComment={() => { if (composer.trim()) { gov.run({ type: 'comment', recordId: rec.id, body: composer.trim() }); setComposer('') } }}
              onUpdate={() => { if (composer.trim()) { gov.run({ type: 'add_update', recordId: rec.id, body: composer.trim(), kind: 'note' }); setComposer('') } }}
            />
          )}
          {tab === 'Analysis' && <AnalysisTab rec={rec} related={related} module={module} health={gov.health(module)} />}
          {tab === 'Related' && <RelatedTab related={related} onOpenRecord={onOpenRecord} />}
          {tab === 'Evidence' && <EvidenceTab evidence={evidence} onOpenRecord={onOpenRecord} />}
          {tab === 'Permissions' && <PermissionsTab role={role} />}
          {tab === 'History' && <HistoryTab rec={rec} />}
        </div>

        {/* Right rail */}
        <aside className="olv-gov-rail" aria-label="Record metadata">
          <Panel>
            <div style={railRowStyle}>
              <span style={railLabelStyle}>Assignee</span>
              <span style={{ fontSize: 13 }}>{rec.assignee ?? 'Unassigned'}</span>
              <InlineSet placeholder="Assign to…" value={assignInput} setValue={setAssignInput} decision={decisionFor('assign')}
                onSubmit={() => { if (assignInput.trim()) { gov.run({ type: 'assign', recordId: rec.id, assignee: assignInput.trim() }); setAssignInput('') } }} />
            </div>
            <div style={railRowStyle}>
              <span style={railLabelStyle}>Reviewers</span>
              <span style={{ fontSize: 13 }}>{rec.reviewers.length ? rec.reviewers.join(', ') : 'None'}</span>
              <InlineSet placeholder="Add reviewer…" value={reviewerInput} setValue={setReviewerInput} decision={decisionFor('add_reviewer')}
                onSubmit={() => { if (reviewerInput.trim()) { gov.run({ type: 'add_reviewer', recordId: rec.id, reviewer: reviewerInput.trim() }); setReviewerInput('') } }} />
            </div>
            <div style={railRowStyle}>
              <span style={railLabelStyle}>Due date</span>
              <span style={{ fontSize: 13 }}>{rec.dueDate ?? 'None'}</span>
              <InlineSet placeholder="YYYY-MM-DD" value={dueInput} setValue={setDueInput} decision={decisionFor('set_due')}
                onSubmit={() => { if (dueInput.trim()) { gov.run({ type: 'set_due', recordId: rec.id, dueDate: dueInput.trim() }); setDueInput('') } }} />
            </div>
            <div style={railRowStyle}>
              <span style={railLabelStyle}>Labels</span>
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {rec.labels.length ? rec.labels.map((l) => <span key={l} className="olv-gov-pill" style={{ color: 'var(--govv-structure)' }}>{l}</span>) : <span style={{ fontSize: 13, color: 'var(--govv-ink-3)' }}>None</span>}
              </span>
              <InlineSet placeholder="Add label…" value={labelInput} setValue={setLabelInput} decision={decisionFor('label')}
                onSubmit={() => { if (labelInput.trim()) { gov.run({ type: 'label', recordId: rec.id, label: labelInput.trim() }); setLabelInput('') } }} />
            </div>
            <div style={railRowStyle}>
              <span style={railLabelStyle}>Policy scope</span>
              <span style={{ fontSize: 13 }}>{gov.state.policy.name} v{gov.state.policy.version}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={railLabelStyle}>Controlled share</span>
              <input aria-label="Share recipient" placeholder="recipient@…" value={shareRecipient} onChange={(e) => setShareRecipient(e.target.value)}
                style={inputStyle} />
              <select aria-label="Share constraint" value={shareConstraint} onChange={(e) => setShareConstraint(e.target.value)} style={inputStyle}>
                <option value="view_only_7d">View-only · 7 days</option>
                <option value="view_only_30d">View-only · 30 days</option>
                <option value="view_only_single">View-only · single-use</option>
                <option value="edit_internal">Edit · internal</option>
              </select>
              <ActionButton label="Share (manual delivery)" decision={decisionFor('share')}
                onClick={() => { if (shareRecipient.trim()) { gov.run({ type: 'share', recordId: rec.id, recipient: shareRecipient.trim(), constraint: shareConstraint }); setShareRecipient('') } }} />
              <span style={{ fontSize: 10, color: 'var(--govv-ink-3)' }}>Delivery is manual — no email is sent.</span>
            </div>
          </Panel>
        </aside>
      </div>
    </div>
  )
}

const inputStyle: CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 12, borderRadius: 'var(--sc-radius-sm)',
  border: '1px solid var(--govv-line)', background: 'var(--sc-surface-2)', color: 'var(--govv-ink)', marginTop: 4,
}

function InlineSet({ placeholder, value, setValue, onSubmit, decision }: {
  placeholder: string; value: string; setValue: (v: string) => void; onSubmit: () => void; decision: PermissionDecision
}) {
  if (!decision.allowed) {
    return <span style={{ fontSize: 10, color: 'var(--govv-ink-3)' }} title={decision.reason}>Locked — {decision.reason}</span>
  }
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
      <input aria-label={placeholder} placeholder={placeholder} value={value} onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSubmit() }} style={{ ...inputStyle, marginTop: 0 }} />
      <button type="button" className="olv-gov-btn" onClick={onSubmit} style={{ padding: '4px 8px', flexShrink: 0 }}>+</button>
    </div>
  )
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--govv-line)' }}>
      <span style={{ fontSize: 12, color: 'var(--govv-ink-3)', fontFamily: 'var(--sc-font-mono)' }}>{k}</span>
      <span style={{ fontSize: 12, color: 'var(--govv-ink)', textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
    </div>
  )
}

function OverviewTab({ rec }: { rec: GovRecord }) {
  const entries = Object.entries(rec.fields)
  return (
    <Panel>
      <Section title="Details">
        <Field k="record_id" v={rec.id} />
        <Field k="correlation_id" v={rec.correlationId} />
        <Field k="actor" v={`${rec.actor} (${rec.actorRole})`} />
        <Field k="created" v={formatWhen(rec.createdAt)} />
        <Field k="updated" v={formatWhen(rec.updatedAt)} />
        {entries.map(([k, v]) => <Field key={k} k={k} v={v} />)}
      </Section>
    </Panel>
  )
}

function ActivityTab({ rec, composer, setComposer, onComment, onUpdate, canComment, canUpdate }: {
  rec: GovRecord; composer: string; setComposer: (v: string) => void; onComment: () => void; onUpdate: () => void; canComment: PermissionDecision; canUpdate: PermissionDecision
}) {
  const timeline = [
    ...rec.updates.map((u) => ({ at: u.at, who: u.actor, body: u.body, kind: `update · ${u.kind}` })),
    ...rec.comments.map((c) => ({ at: c.at, who: c.actor, body: c.body, kind: 'comment' })),
  ].sort((a, b) => a.at.localeCompare(b.at))
  return (
    <Panel>
      <Section title="Activity & discussion">
        {timeline.length === 0 && <p style={{ color: 'var(--govv-ink-3)', fontSize: 13, margin: 0 }}>No comments or updates yet.</p>}
        {timeline.map((t, i) => (
          <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--govv-line)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{t.who}</span>
              <span className="olv-gov-pill" style={{ color: 'var(--govv-ink-3)' }}>{t.kind}</span>
              <span style={{ fontSize: 11, color: 'var(--govv-ink-3)' }}>{formatWhen(t.at)}</span>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--govv-ink-2)' }}>{t.body}</p>
          </div>
        ))}
      </Section>
      <div style={{ marginTop: 12 }}>
        {canComment.allowed || canUpdate.allowed ? (
          <>
            <textarea aria-label="Comment or update" placeholder="Add a comment or update — @mention supported" value={composer}
              onChange={(e) => setComposer(e.target.value)} rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--sc-font-ui)' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <ActionButton label="Comment" decision={canComment} variant="primary" onClick={onComment} />
              <ActionButton label="Post update" decision={canUpdate} onClick={onUpdate} />
            </div>
          </>
        ) : (
          <p style={{ fontSize: 12, color: 'var(--govv-ink-3)' }}>Locked — {canComment.reason}</p>
        )}
      </div>
    </Panel>
  )
}

function AnalysisTab({ rec, related, module, health }: { rec: GovRecord; related: GovRecord[]; module: GovModule; health: string }) {
  const openRisks = related.filter((r) => r.module === 'risks' && r.statusKind === 'attention').length
  const receipts = related.filter((r) => r.module === 'receipts').length
  const integrity = related.filter((r) => r.module === 'integrity').length
  return (
    <Panel>
      <Section title="Deterministic analysis">
        <p style={{ fontSize: 12, color: 'var(--govv-ink-3)', margin: '0 0 10px' }}>
          Computed from the local-session ledger — not an AI inference.
        </p>
        <Field k="correlated_records" v={String(related.length)} />
        <Field k="linked_receipts" v={String(receipts)} />
        <Field k="linked_integrity_blocks" v={String(integrity)} />
        <Field k="open_related_risks" v={String(openRisks)} />
        <Field k="module_health" v={health} />
        <Field k="severity" v={rec.severity} />
        <Field k="module" v={GOV_MODULE_DEFS[module].title} />
      </Section>
    </Panel>
  )
}

function RelatedTab({ related, onOpenRecord }: { related: GovRecord[]; onOpenRecord: (m: GovModule, id: string) => void }) {
  return (
    <Panel>
      <Section title={`Related records (${related.length})`}>
        {related.length === 0 && <p style={{ color: 'var(--govv-ink-3)', fontSize: 13, margin: 0 }}>No correlated records.</p>}
        {related.map((r) => (
          <button key={r.id} type="button" className="olv-gov-row" onClick={() => onOpenRecord(r.module, r.id)}
            style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 10, alignItems: 'center', padding: '8px 6px', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: '1px solid var(--govv-line)' }}>
            <span className="olv-gov-pill" style={{ color: STATUS_KIND_VAR[r.statusKind] }}>{GOV_MODULE_DEFS[r.module].title}</span>
            <span style={{ fontSize: 13, color: 'var(--govv-ink)', flex: 1 }}>{r.title}</span>
            <span style={{ fontSize: 11, color: 'var(--govv-ink-3)' }}>{r.status}</span>
          </button>
        ))}
      </Section>
    </Panel>
  )
}

function EvidenceTab({ evidence, onOpenRecord }: { evidence: GovRecord[]; onOpenRecord: (m: GovModule, id: string) => void }) {
  return (
    <Panel>
      <Section title={`Evidence bundle (${evidence.length})`}>
        <p style={{ fontSize: 12, color: 'var(--govv-ink-3)', margin: '0 0 10px' }}>
          Receipts + integrity blocks correlated to this record. Bundling is assembled locally; export/delivery is manual.
        </p>
        {evidence.length === 0 && <p style={{ color: 'var(--govv-ink-3)', fontSize: 13, margin: 0 }}>No linked evidence yet.</p>}
        {evidence.map((r) => (
          <button key={r.id} type="button" className="olv-gov-row" onClick={() => onOpenRecord(r.module, r.id)}
            style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 10, alignItems: 'center', padding: '8px 6px', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: '1px solid var(--govv-line)' }}>
            <span className="olv-gov-pill" style={{ color: STATUS_KIND_VAR[r.statusKind] }}>{GOV_MODULE_DEFS[r.module].title}</span>
            <span style={{ fontSize: 13, flex: 1 }}>{r.title}</span>
          </button>
        ))}
      </Section>
    </Panel>
  )
}

function PermissionsTab({ role }: { role: string }) {
  const actions: GovAction[] = ['view', 'comment', 'add_update', 'assign', 'approve', 'deny', 'revoke', 'share', 'change_policy']
  return (
    <Panel>
      <Section title={`Effective permissions · acting as ${role}`}>
        <p style={{ fontSize: 12, color: 'var(--govv-ink-3)', margin: '0 0 10px' }}>
          deny-overrides-allow. Use Test-as-role to preview another role.
        </p>
        {actions.map((a) => {
          const d = evaluatePermission(role as never, a)
          return (
            <div key={a} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--govv-line)' }}>
              <span style={{ fontSize: 12, fontFamily: 'var(--sc-font-mono)' }}>{a}</span>
              <span style={{ fontSize: 12, color: d.allowed ? 'var(--govv-verified)' : 'var(--govv-denied)' }} title={d.reason}>
                {d.allowed ? 'allow' : 'deny'}
              </span>
            </div>
          )
        })}
      </Section>
    </Panel>
  )
}

function HistoryTab({ rec }: { rec: GovRecord }) {
  return (
    <Panel>
      <Section title={`History (${rec.history.length})`}>
        {rec.history.map((h) => (
          <div key={h.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--govv-line)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{h.action}</span>
              <span style={{ fontSize: 11, color: 'var(--govv-ink-3)' }}>{h.actor} · {formatWhen(h.at)}</span>
            </div>
            {h.detail && <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--govv-ink-2)' }}>{h.detail}</p>}
          </div>
        ))}
      </Section>
    </Panel>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 style={{ margin: '0 0 10px', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--govv-ink-3)', fontFamily: 'var(--sc-font-mono)' }}>{title}</h3>
      {children}
    </section>
  )
}
